import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ReactiveFormsModule,
  UntypedFormBuilder,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatButtonToggleChange,
  MatButtonToggleModule,
} from '@angular/material/button-toggle';
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule, MatHint } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  MatRadioChange,
  MatRadioGroup,
  MatRadioModule,
} from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import currency from 'currency.js';
import { firstValueFrom } from 'rxjs';

import { DealService } from '../../../../modules/advance-application/services/deal.service';
import { V1IBankAccount } from '../../../../modules/authentication/models/v1-api.interface';
import { AuthenticationService } from '../../../../modules/authentication/services/authentication.service';
import { CurrencyInputComponent } from '../../../../modules/currency-input/currency-input.component';
import { DayInputComponent } from '../../../../modules/day-input/day-input.component';
import { CurrencyJsPipe } from '../../../../modules/pipes/currency-js.pipe';
import { DatePipe } from '../../../../modules/pipes/date.pipe';
import { LocalisePipe } from '../../../../modules/pipes/localise.pipe';
import { DisableAutofillDirective } from '../../../../modules/shared/directives/disable-autofill.directive';
import { Day } from '../../../../modules/shared/models/day.type';
import { FormatDateEnum } from '../../../../modules/shared/models/format-date.enum';
import {
  DateVariantEnum,
  IDealForPay,
} from '../../../../modules/shared/models/init-schedule-payment.interface';
import { TransactionAppEnum } from '../../../../modules/shared/models/transaction-app.enum';
import { AccountService } from '../../../../modules/shared/services/account.service';
import { BankService } from '../../../../modules/shared/services/bank.service';
import { DateService } from '../../../../modules/shared/services/date.service';
import { PlaidService } from '../../../../modules/shared/services/plaid.service';
import { WorkloadIndicator } from '../../../../modules/shared/services/progress-bar.service';
import { PromisePaymentService } from '../../../../modules/shared/services/promise-payment.service';
import { SharedService } from '../../../../modules/shared/services/shared.service';
import { ThemesService } from '../../../../services/themes.service';
import { sleep } from '../../../../tools/sleep.tools';
import { RemoveSubstringPipe } from './remove-substring.pipe';

enum SchedulePaymentStepEnum {
  Deal,
  Date,
  Amount,
  Done,
}

enum AmountVariantEnum {
  deal,
  full,
  custom,
}

@Component({
  selector: 'app-schedule-payment',
  templateUrl: './schedule-payment.component.html',
  styleUrls: ['./schedule-payment.component.scss'],
  providers: [WorkloadIndicator],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatButtonToggleModule,
    MatRadioModule,
    MatFormFieldModule,
    MatInputModule,
    MatHint,
    MatProgressSpinnerModule,
    MatTooltipModule,
    CurrencyInputComponent,
    DayInputComponent,
    LocalisePipe,
    CurrencyJsPipe,
    DatePipe,
    RemoveSubstringPipe,
    DisableAutofillDirective,
  ],
})
export class SchedulePaymentComponent implements OnInit {
  @Input() dealId: number;

  @ViewChild('dateInput')
  dateInput: DayInputComponent;

  @ViewChild('bankAccountInput')
  bankAccountInput: MatRadioGroup;

  @ViewChild('amountInput')
  amountInput: CurrencyInputComponent;

  public dealDetails: IDealForPay;
  public selectedBankAccount: V1IBankAccount;
  public deals: IDealForPay[];
  public error: string;
  public fullAmount = currency(0);
  public dealAmount = currency(0);
  public user = this.authenticationService.profile;
  public bankList: V1IBankAccount[] = [];
  public bankMap: Record<number, V1IBankAccount> = {};
  public step = SchedulePaymentStepEnum.Deal;
  public withoutSelectingDeal = false;
  public stepEnum = SchedulePaymentStepEnum;
  public dateVariant = DateVariantEnum.origin;
  public amountVariant = AmountVariantEnum.deal;
  public lastAmountVariantCustom = false;
  public amountVariantEnum = AmountVariantEnum;
  public dateVariantEnum = DateVariantEnum;
  public formatDateEnum = FormatDateEnum;
  public paymentDate: Day;
  public closingDate: Day;
  public minPaymentDate: Date;
  public allowPayTodayExpire = 0;
  public allowPayTodayExpireTimeout: number;
  public minAmount = currency(0);
  public maxAmount = currency(0);
  public tomorrowDate = this.dateService.dateToYyyymmdd(
    this.dateService.addDaysToDate(new Date(), 1),
  );
  public todayDate = this.dateService.dateToYyyymmdd(new Date());
  public form = this.formBuilder.group({
    bankAccountId: [
      undefined,
      [
        (control: AbstractControl<number>) => {
          if (!control.value) {
            return { error: 'required' };
          }
          const bankAccount = this.bankMap[control.value];
          if (bankAccount?.plaidConnection?.disconnectedAt) {
            return { error: 'disconnected' };
          }
          if (!bankAccount) {
            return { error: 'required' };
          }

          return null;
        },
      ],
    ],
    dealId: [undefined, [Validators.required]],
    selectedDeal: [undefined],
    amount: [
      undefined,
      [
        Validators.required,
        (control: AbstractControl) => {
          const value: currency | undefined = control.value;
          if (!value?.value && value?.value !== 0) {
            return null;
          }

          if (value.value > this.maxAmount.value) {
            return {
              error: `the maximum value is ${this.maxAmount.format()}`,
            };
          }
          if (value.value < this.minAmount.value) {
            return {
              error: `the minimum value is ${this.minAmount.format()}`,
            };
          }

          return null;
        },
      ],
    ],
    date: [undefined, [Validators.required]],
  });

  constructor(
    private accountService: AccountService,
    private authenticationService: AuthenticationService,
    private dateService: DateService,
    private dealService: DealService,
    private plaidService: PlaidService,
    private promisePaymentService: PromisePaymentService,
    private bankService: BankService,
    private formBuilder: UntypedFormBuilder,
    private sharedService: SharedService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<SchedulePaymentComponent>,
    public themesService: ThemesService,
    public workloadIndicator: WorkloadIndicator,
  ) {}

  ngOnInit(): void {
    this.initFormValue();
  }

  public async setStep(stepNumber: SchedulePaymentStepEnum): Promise<void> {
    this.workloadIndicator.start();

    try {
      if (stepNumber === SchedulePaymentStepEnum.Amount) {
        const paymentDate = this.form.controls.date.value;
        await Promise.all([
          this.loadBankAccounts(),
          this.loadAmounts(paymentDate),
        ]);

        switch (this.amountVariant) {
          case AmountVariantEnum.deal:
            await this.setDealAmount();
            break;
          case AmountVariantEnum.full:
            await this.setFullBalanceAmount();
            break;
          case AmountVariantEnum.custom:
            await this.onAmountVariantCustom();
            break;
        }
      }

      this.step = stepNumber;
    } catch (e) {
      this.error = this.sharedService.getErrorText(e, true);
    }

    this.workloadIndicator.stop();
  }

  public async initFormValue(): Promise<void> {
    this.workloadIndicator.start();

    try {
      const user = await firstValueFrom(this.user);
      const result = await firstValueFrom(
        this.promisePaymentService.getInitValuesForPayment(),
      );
      this.form.get('dealId')?.setValue(result.dealId);
      this.form.get('selectedDeal')?.setValue(result.dealId);

      this.deals = result.allDeals;
      this.allowPayTodayExpire = result.allowPayTodayExpire;
      this.minAmount = result.minAmount;
      this.closingDate = result.closingDate;
      this.paymentDate = result.paymentDate;
      this.withoutSelectingDeal = result.withoutSelectingDeal;
      this.dateVariant = result.initDateVariant;
      this.minPaymentDate = this.dateService.parseAnyDateFormatToDate(
        result.minPaymentDate,
      );
      if (result.withoutSelectingDeal) {
        this.step = SchedulePaymentStepEnum.Date;
      }

      this.form.get('date')?.setValue(this.paymentDate);
      this.form.get('bankAccountId')?.setValue(user?.selectedBankAccountId);

      if (this.allowPayTodayExpireTimeout) {
        clearTimeout(this.allowPayTodayExpireTimeout);
      }

      if (this.allowPayTodayExpire > 0) {
        this.allowPayTodayExpireTimeout = setTimeout(() => {
          this.allowPayTodayExpire = 0;
        }, this.allowPayTodayExpire * 1000);
      }
    } catch (e) {
      this.error = this.sharedService.getErrorText(e, true);
    }

    this.workloadIndicator.stop();
  }

  async loadAmounts(onDate: Day): Promise<void> {
    const result = await firstValueFrom(
      this.promisePaymentService.calcAmountsForPayment(
        onDate,
        this.form.controls.dealId.value,
      ),
    );

    this.maxAmount = result.maxAmount;
    this.fullAmount = result.fullAmount;
    this.dealAmount = result.dealAmount;
    this.minAmount = result.minAmount;
  }

  async loadBankAccounts(): Promise<void> {
    const user = await firstValueFrom(this.user);

    // if (!!user?.plaidConnections?.length) {
    //   try {
    //     await Promise.all(
    //       user.plaidConnections.map((c) =>
    //         firstValueFrom(this.plaidService.sync(c.id)),
    //       ),
    //     );
    //   } catch (e) {}
    // }

    const bankListResult = await firstValueFrom(this.bankService.getList());
    this.bankList = bankListResult || [];
    this.bankMap = {};
    this.bankList.forEach((bank) => (this.bankMap[bank.id] = bank));
    this.changedBankAccount({
      value: this.form.controls.bankAccountId.value,
    } as MatRadioChange);
  }

  async send(): Promise<void> {
    this.workloadIndicator.start();

    try {
      const row = this.form.getRawValue();
      await firstValueFrom(
        this.promisePaymentService.createPromisePayment({
          ...row,
          isFinalPayment: false,
          transactionApp: TransactionAppEnum.Web,
        }),
      );
      this.close(true);
    } catch (e) {
      this.error = this.sharedService.getErrorText(e, true);
      this.workloadIndicator.stop();
    }
  }

  public close(result?: boolean): void {
    this.dialogRef.close(result);
  }

  setDateTomorrow(): void {
    this.form.controls.date.setValue(this.tomorrowDate);
  }

  setDateToday(): void {
    this.form.controls.date.setValue(this.todayDate);
  }

  setPaymentDate(): void {
    this.form.controls.date.setValue(this.paymentDate);
  }

  onDateVariantCustom(): void {
    this.cdr.detectChanges();
    this.dateInput.el.nativeElement.querySelector('input')?.focus();
  }

  onAmountVariantChange(ev: AmountVariantEnum): void {
    if (this.amountVariant === ev || ev === undefined) {
      return;
    }

    this.amountVariant = ev;
    this.lastAmountVariantCustom = false;
  }

  async onAmountVariantCustom(): Promise<void> {
    if (this.lastAmountVariantCustom) {
      return;
    }

    this.lastAmountVariantCustom = true;
    this.cdr.detectChanges();
    const el = this.amountInput.el.nativeElement.querySelector('input');
    el?.focus();
    await sleep(500);
    el?.select();
  }

  setFullBalanceAmount(): void {
    this.form.get('amount')?.setValue(this.fullAmount);
  }

  async setDealAmount(): Promise<void> {
    this.form.get('amount')?.setValue(this.dealAmount);
  }

  public onChangeDeal(event: MatButtonToggleChange): void {
    if (!this.deals.length) {
      return;
    }
    let value = event.value;
    if (value === -1) {
      value = this.deals[0].id;
    }

    const deal = this.deals.find((d) => d.id === value);
    if (!deal) {
      return;
    }

    this.dealDetails = deal;
    this.form.get('dealId')?.setValue(deal.id);
    this.paymentDate = deal.paymentDate;
    this.form.get('date')?.setValue(this.paymentDate);
  }

  public addNewBankAccount(): void {
    this.bankService.addNewBankAccountModal(this.dialog).subscribe((result) => {
      if (typeof result === 'object') {
        this.selectNewBankAccountFromModal(result);
      } else {
        this.closeNewBankAccountFromModal(result);
      }
    });
  }

  public selectNewBankAccountFromModal(bank: V1IBankAccount): void {
    this.bankList.push(bank);
    this.form.get('bankAccountId')?.setValue(bank.id);
  }

  public async closeNewBankAccountFromModal(result: boolean): Promise<void> {
    if (result) {
      this.workloadIndicator.start();
      await this.loadBankAccounts();
      // this.bankAccountInput.open();
      this.workloadIndicator.stop();
    }
  }

  public changedBankAccount(value: MatRadioChange): void {
    const bank = this.bankList.find((b) => b.id === value.value);
    if (!bank) {
      return;
    }

    this.selectedBankAccount = bank;
  }
}
